// screens/RadarTopics.tsx — R3b (plate 04): the topics window. Each
// tracked topic's state, its buttons, and the add-a-topic form.
//
// R3b-2 (2026-09-27): pythia_radar_topic_write's ACTUAL input schema now
// DOES take `cadence {every,at[,day]}` and `notify` (validated server-side
// — a malformed shape is a loud refusal), so the add-a-topic form's choice
// is forwarded for real. Cadence + next run render on every row (shared
// with RadarHome's card via lib/radarCadence). A topic whose newest run is
// still going ("researching") shows a live card and this screen polls the
// list every 30s while one is — the same `last_run` field the list already
// carries, no extra `topic_get` call per topic.
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  createRadarTopic,
  getRadarTopic,
  listRadarTopics,
  researchRadarTopicNow,
  setRadarTopicTracking,
  updateRadarTopic,
  type RadarTopicListApi,
} from '../lib/api';
import { BackLink } from '../components/BackLink';
import { CADENCE_OPTIONS, cadenceForChoice, cadenceLine, nextRunLabel, type CadenceEvery } from '../lib/radarCadence';
import { isResearching, topicFreshnessLine } from '../lib/radarFreshness';
import './RadarTopics.css';

export interface RadarTopicsProps {
  tenantId: string;
  onBack: () => void;
  onOpenTopic: (slug: string) => void;
  // R3e (C) — linked from the topics window too (spec).
  onOpenNewsletter: () => void;
}

type LoadState = { phase: 'loading' } | { phase: 'error'; message: string } | { phase: 'ready' };

const RESEARCH_POLL_MS = 30_000;

export function RadarTopics({ tenantId, onBack, onOpenTopic, onOpenNewsletter }: RadarTopicsProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [topics, setTopics] = useState<RadarTopicListApi[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [researching, setResearching] = useState<Record<string, string>>({});
  const [note, setNote] = useState<string | null>(null);
  const [receiptNote, setReceiptNote] = useState<string | null>(null);
  const [confirmArchive, setConfirmArchive] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [consequence, setConsequence] = useState('');
  const [queries, setQueries] = useState('');
  const [exclusions, setExclusions] = useState('');
  const [cadence, setCadence] = useState<CadenceEvery>('daily');

  function refresh() {
    return listRadarTopics(tenantId)
      .then((t) => {
        setTopics(t);
        // A topic no longer researching drops its transient "researching ·
        // <receipt>" line — the card becomes the topic's normal line
        // (R3b-2 item 6).
        setResearching((cur) => {
          const still = t.filter((x) => isResearching(x.pendingCommand, x.lastRun)).map((x) => x.slug);
          const next: Record<string, string> = {};
          for (const slug of still) if (cur[slug]) next[slug] = cur[slug];
          return next;
        });
        setLoad({ phase: 'ready' });
      })
      .catch((err: unknown) => {
        setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the radar is not reachable' });
      });
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Poll every 30s while any topic's `pending_command` is queued/running —
  // the same field the list already carries (R3b-3 item C1).
  useEffect(() => {
    if (load.phase !== 'ready') return;
    if (!topics.some((x) => isResearching(x.pendingCommand, x.lastRun))) return;
    const id = setInterval(() => void refresh(), RESEARCH_POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load.phase, topics]);

  async function toggleTracking(t: RadarTopicListApi) {
    setBusy(t.slug);
    setNote(null);
    setReceiptNote(null);
    try {
      const cmd = await setRadarTopicTracking(tenantId, t.slug, t.state !== 'tracking');
      if (cmd.receipt) setReceiptNote(cmd.receipt);
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not recorded');
    } finally {
      setBusy(null);
    }
  }

  async function researchNow(slug: string) {
    setBusy(slug);
    setNote(null);
    try {
      const cmd = await researchRadarTopicNow(tenantId, slug);
      setResearching((cur) => ({ ...cur, [slug]: cmd.receipt ?? cmd.status }));
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not recorded');
    } finally {
      setBusy(null);
    }
  }

  // C2 (R3b-3): a topic needs at least one search word to be researched —
  // pythia_radar_topic_write refuses `state:'tracking'` or a research_now
  // command on a topic whose `queries` is empty (P3's own sentence, shown
  // verbatim below if it ever slips past this client-side gate).
  const trimmedQueries = queries.split('\n').map((q) => q.trim()).filter(Boolean);

  async function addTopic(mode: 'research' | 'track') {
    const trimmedName = name.trim();
    const trimmedConsequence = consequence.trim();
    if (!trimmedName || !trimmedConsequence || trimmedQueries.length === 0) return;
    setBusy('__add__');
    setNote(null);
    try {
      const { topic } = await createRadarTopic(tenantId, {
        name: trimmedName,
        consequenceMd: trimmedConsequence,
        queries: trimmedQueries,
        exclusions: exclusions.split('\n').map((q) => q.trim()).filter(Boolean),
        state: mode === 'track' ? 'tracking' : 'once',
        cadence: cadenceForChoice(cadence),
        notify: false,
      });
      if (mode === 'research') {
        const cmd = await researchRadarTopicNow(tenantId, topic.slug);
        setResearching((cur) => ({ ...cur, [topic.slug]: cmd.receipt ?? cmd.status }));
      }
      setName('');
      setConsequence('');
      setQueries('');
      setExclusions('');
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not added');
    } finally {
      setBusy(null);
    }
  }

  // Archive — the only "remove" a topic has (its items and runs stay put,
  // R3b-2 item 5). The write requires the topic's full body, which this
  // row-level list doesn't carry, so this reads it once first. Never a
  // native confirm() — the row's own inline confirmation gates the call.
  async function archiveTopic(t: RadarTopicListApi) {
    setBusy(t.slug);
    setNote(null);
    setReceiptNote(null);
    try {
      const full = await getRadarTopic(tenantId, t.slug);
      const { receipt } = await updateRadarTopic(tenantId, t.slug, {
        name: full.topic.name,
        consequenceMd: full.topic.consequenceMd,
        queries: full.topic.queries,
        exclusions: full.topic.exclusions,
        geo: full.topic.geo ?? undefined,
        languages: full.topic.languages ?? undefined,
        state: 'archived',
        cadence: full.topic.cadence as unknown as { every: CadenceEvery; at: string; day?: number } | undefined,
      });
      setConfirmArchive(null);
      // R3b-3 (P4/C1): the tool's own receipt, verbatim — the client-composed
      // fallback only covers a Pythia build that predates P4 (writeTopic
      // answers receipt:null then).
      setReceiptNote(receipt ?? `archived — ${t.name}; its items and runs stay in the record`);
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not archived');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div class="sp">
      <BackLink label="Radar" onClick={onBack} />
      <div class="sp__eyebrow">Topics</div>
      {/* R3e (C) — linked from the topics window too (spec). */}
      <button type="button" class="rtopics__newsletter-link" onClick={onOpenNewsletter}>Newsletter</button>

      {load.phase === 'loading' ? (
        <div class="sp__say">One moment…</div>
      ) : load.phase === 'error' ? (
        <div class="sp__say">
          The radar is not reachable.
          <br />
          <span class="sp__say-soft">{load.message}</span>
        </div>
      ) : (
        <>
          {topics.length === 0 ? (
            <div class="sp__empty">No topics yet. Add one below.</div>
          ) : (
            topics.map((t) => (
              <div class="rtopics__row" key={t.slug}>
                <button type="button" class="rtopics__row-main" onClick={() => onOpenTopic(t.slug)}>
                  <span class="rtopics__row-name">{t.name}</span>
                  <span class="rtopics__row-kind">
                    {cadenceLine(t.state, t.cadence)} · {nextRunLabel(t.nextDueAt)}
                  </span>
                  <span class="rtopics__row-fresh">{topicFreshnessLine(t.lastDiscover, t.lastRun)}</span>
                </button>
                {confirmArchive === t.slug ? (
                  <div class="rtopics__confirm">
                    <span>Remove "{t.name}"? Its items and runs stay in the record.</span>
                    <button type="button" class="sp__act" disabled={busy === t.slug} onClick={() => void archiveTopic(t)}>
                      Yes, remove
                    </button>
                    <button type="button" class="sp__act" onClick={() => setConfirmArchive(null)}>
                      Never mind
                    </button>
                  </div>
                ) : (
                  <div class="rtopics__row-acts">
                    <button type="button" class="sp__act" disabled={busy === t.slug} onClick={() => void researchNow(t.slug)}>
                      Research now
                    </button>
                    <button type="button" class="sp__act" onClick={() => onOpenTopic(t.slug)}>
                      Edit
                    </button>
                    <button type="button" class="sp__act" disabled={busy === t.slug} onClick={() => void toggleTracking(t)}>
                      {t.state === 'tracking' ? 'Pause tracking' : 'Add to tracking'}
                    </button>
                    <button type="button" class="sp__act" disabled={busy === t.slug} onClick={() => setConfirmArchive(t.slug)}>
                      Remove
                    </button>
                  </div>
                )}
                {isResearching(t.pendingCommand, t.lastRun) ? (
                  <div class="rtopics__researching">
                    researching · started {t.pendingCommand?.startedAt
                      ? new Date(t.pendingCommand.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                      : t.lastRun?.startedAt
                        ? new Date(t.lastRun.startedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                        : 'just now'}
                    {researching[t.slug] ? ` · ${researching[t.slug]}` : ''}
                  </div>
                ) : researching[t.slug] ? (
                  <div class="rtopics__researching">researching · {researching[t.slug]}</div>
                ) : null}
              </div>
            ))
          )}

          {receiptNote ? <div class="sp__age">{receiptNote}</div> : null}
          {note ? <div class="sp__age sp__age--late">{note}</div> : null}

          <div class="sp__sec" style={{ marginTop: '20px' }}>
            <span>Add a topic</span>
          </div>
          <div class="rtopics__form">
            <label class="rtopics__label" for="rtopics-name">name</label>
            <input id="rtopics-name" class="rtopics__input" type="text" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />

            <label class="rtopics__label" for="rtopics-consequence">what would change our plans</label>
            <textarea
              id="rtopics-consequence"
              class="rtopics__input"
              rows={3}
              value={consequence}
              onInput={(e) => setConsequence((e.target as HTMLTextAreaElement).value)}
            />

            <label class="rtopics__label" for="rtopics-queries">words to search (one per line)</label>
            <textarea
              id="rtopics-queries"
              class="rtopics__input"
              rows={2}
              required
              aria-required="true"
              value={queries}
              onInput={(e) => setQueries((e.target as HTMLTextAreaElement).value)}
            />

            <label class="rtopics__label" for="rtopics-exclusions">leave out (one per line)</label>
            <textarea
              id="rtopics-exclusions"
              class="rtopics__input"
              rows={2}
              value={exclusions}
              onInput={(e) => setExclusions((e.target as HTMLTextAreaElement).value)}
            />

            <label class="rtopics__label" for="rtopics-cadence">cadence</label>
            <select
              id="rtopics-cadence"
              class="rtopics__input"
              value={cadence}
              onChange={(e) => setCadence((e.target as HTMLSelectElement).value as typeof cadence)}
            >
              {CADENCE_OPTIONS.map((c) => (
                <option value={c.id} key={c.id}>{c.label}</option>
              ))}
            </select>

            <div class="rtopics__form-acts">
              <button
                type="button"
                class="sp__act"
                disabled={busy === '__add__' || !name.trim() || !consequence.trim() || trimmedQueries.length === 0}
                onClick={() => void addTopic('research')}
              >
                Research now
              </button>
              <button
                type="button"
                class="sp__act"
                disabled={busy === '__add__' || !name.trim() || !consequence.trim() || trimmedQueries.length === 0}
                onClick={() => void addTopic('track')}
              >
                Add to tracking
              </button>
            </div>
            <div class="rtopics__form-soft">
              Research now runs once and shows results here. Tracking polls on the schedule and keeps finding sources.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
